import { html, nothing, unsafeCSS, type CSSResultGroup, type PropertyValues } from 'lit';
import { live } from 'lit/directives/live.js';
import { customElement, property, state } from 'lit/decorators.js';
import { LibElement } from '../lib-element';
import css from '../textbox/lib-textbox.css';

/** String-only editing shell; .NET owns all numeric operations. */
@customElement('lib-number-box')
export class LibNumberBox extends LibElement {
  protected override get hostFocusSelector(): string { return 'input'; }
  static override readonly styles: CSSResultGroup = [LibElement.hostStyles, unsafeCSS(css)];
  /** Includes editable text in native HTML forms without waiting for .NET parsing. */
  static readonly formAssociated = true;
  private readonly internals = typeof this.attachInternals === 'function' ? this.attachInternals() : undefined;
  /** Native form field name. */
  @property() name = '';
  /** Identity of the input and its internal label target. */
  @property({ attribute: 'input-id' }) inputId = '';
  /** Autofill hint forwarded to the input. */
  @property() autocomplete = '';
  /** Focuses the enabled input after its first render. */
  @property({ type: Boolean }) autofocus = false;
  @property() value = '';
  @property() display = '';
  @property() revision = '0';
  @property() response = '';
  @property() min = '';
  @property() max = '';
  @property({ attribute: 'aria-value' }) ariaValue = '';
  @property({ attribute: 'aria-min' }) ariaMin = '';
  @property({ attribute: 'aria-max' }) ariaMax = '';
  @property({ attribute: 'number-label' }) numberLabel = 'Number';
  @property({ attribute: 'increase-label' }) increaseLabel = 'Increase';
  @property({ attribute: 'decrease-label' }) decreaseLabel = 'Decrease';
  @property() headline = '';
  @property() placeholder = '';
  @property() message = '';
  @property() surface = 'white';
  @property({ attribute: 'aria-label' }) override ariaLabel: string | null = null;
  @property({ type: Boolean }) error = false;
  @property({ type: Boolean, attribute: 'parse-invalid' }) parseInvalid = false;
  @property({ type: Boolean }) disabled = false;
  @property({ type: Boolean }) readonly = false;
  @property({ type: Boolean, attribute: 'show-spin-buttons' }) showSpinButtons = false;
  @property({ type: Boolean, attribute: 'allow-mouse-wheel' }) allowMouseWheel = false;
  @property({ attribute: 'bind-mode' }) bindMode = 'onlostfocus';
  @state() private draft = '';
  @state() private focused = false;
  @state() private formDisabled = false;
  private dirty = false;
  private sequence = 0;
  private latest = '';
  private timer?: ReturnType<typeof setTimeout>;
  private get effectivelyDisabled(): boolean { return this.disabled || this.formDisabled; }

  protected override willUpdate(changed: PropertyValues<this>): void {
    if (changed.has('revision')) {
      this.cancel();
      this.dirty = false;
      this.draft = this.focused ? this.value : this.display || this.value;
    } else if ((changed.has('value') || changed.has('display') || changed.has('response')) && this.response === this.latest && (!this.focused || !this.dirty)) {
      this.draft = this.focused ? this.value : this.display || this.value;
    }
    if (changed.has('disabled') || changed.has('readonly') || changed.has('bindMode')) this.cancel();
    this.syncFormValue();
  }
  protected override firstUpdated(): void {
    if (this.autofocus && !this.effectivelyDisabled)
      this.renderRoot.querySelector('input')?.focus();
  }
  /** Restores the current value attribute and clears pending numeric validation. */
  formResetCallback(): void {
    this.cancel();
    this.latest = String(++this.sequence);
    this.dirty = false;
    this.value = this.getAttribute('value') ?? '';
    this.display = this.getAttribute('display') ?? this.value;
    this.draft = this.focused ? this.value : this.display;
    this.syncFormValue();
    this.dispatchEvent(new CustomEvent('lib-number-box', {
      bubbles: true, composed: true,
      detail: { text: this.value, action: 'reset', fromDraft: false, revision: this.revision, request: this.latest },
    }));
  }
  /** Applies disabled-fieldset state to the editor and its actions. */
  formDisabledCallback(disabled: boolean): void {
    this.formDisabled = disabled;
    if (disabled) this.cancel();
    this.syncFormValue();
  }
  private syncFormValue(): void {
    const value = this.dirty ? this.draft : this.value;
    if (typeof this.internals?.setFormValue === 'function')
      this.internals.setFormValue(this.effectivelyDisabled ? null : value);
  }
  private cancel(): void { clearTimeout(this.timer); this.timer = undefined; }
  override disconnectedCallback(): void {
    this.cancel();
    this.dirty = false;
    this.focused = false;
    this.draft = this.display;
    this.latest = String(++this.sequence);
    super.disconnectedCallback();
  }
  private send(action: string): void {
    this.cancel();
    if (!this.isConnected || this.effectivelyDisabled || this.readonly) return;
    this.latest = String(++this.sequence);
    const text = action !== 'input' && action !== 'commit' && action !== 'validate' && !this.dirty ? this.value : this.draft;
    this.dispatchEvent(new CustomEvent('lib-number-box', {
      bubbles: true, composed: true,
      detail: { text, action, fromDraft: this.dirty, revision: this.revision, request: this.latest },
    }));
    if (['up', 'down', 'up10', 'down10'].includes(action)) this.dirty = false;
  }
  private input(event: Event): void {
    event.stopPropagation();
    if (this.effectivelyDisabled || this.readonly) return;
    this.draft = (event.target as HTMLInputElement).value;
    this.dirty = true;
    this.syncFormValue();
    // Invalidate older responses immediately, before a delayed request is sent.
    this.latest = String(++this.sequence);
    this.cancel();
    if (this.bindMode === 'oninput') this.send('input');
    else {
      this.send('validate');
      if (this.bindMode === 'ondelayedinput') this.timer = setTimeout(() => this.send('input'), 300);
    }
  }
  private flush(): void {
    this.cancel();
    if (this.dirty) { this.send('commit'); this.dirty = false; }
    if (!this.focused) this.draft = this.display;
  }
  private key(event: KeyboardEvent): void {
    if (this.effectivelyDisabled || this.readonly) return;
    if (event.key === 'Enter') { event.preventDefault(); this.flush(); }
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault();
      this.send((event.key === 'ArrowUp' ? 'up' : 'down') + (event.shiftKey ? '10' : ''));
    }
  }
  private wheel(event: WheelEvent): void {
    if (!this.allowMouseWheel || !this.focused || this.effectivelyDisabled || this.readonly || !event.deltaY) return;
    event.preventDefault();
    this.send(event.deltaY < 0 ? 'up' : 'down');
  }
  protected override render() {
    const disabled = this.effectivelyDisabled;
    const inputId = this.inputId || 'input';
    return html`<div class="lib-textbox lib-textbox-surface-${this.surface} ${disabled ? 'lib-textbox-disabled' : ''} ${this.readonly ? 'lib-textbox-readonly' : ''} ${this.error ? 'lib-textbox-error' : ''}">
      ${this.headline ? html`<div class="lib-textbox-label"><label class="lib-textbox-label-text" for=${inputId}>${this.headline}</label><slot name="help"></slot></div>` : nothing}
      <div class="lib-textbox-field">
        <input id=${inputId} name=${this.name || nothing} autocomplete=${this.autocomplete || nothing}
          class="lib-textbox-input" type="text" inputmode="decimal" role="spinbutton" .value=${live(this.draft)}
          placeholder=${this.placeholder} ?disabled=${disabled} ?readonly=${this.readonly}
          aria-label=${this.ariaLabel || this.headline || this.numberLabel}
          aria-valuenow=${this.ariaValue || nothing} aria-valuetext=${this.display || nothing}
          aria-valuemin=${this.ariaMin || nothing} aria-valuemax=${this.ariaMax || nothing}
          aria-invalid=${this.error ? 'true' : 'false'} aria-describedby=${this.message ? 'message' : nothing}
          @input=${this.input} @keydown=${this.key} @wheel=${this.wheel}
          @focus=${() => { this.focused = true; this.draft = this.value; }}
          @blur=${() => {
            this.focused = false;
            queueMicrotask(() => { if (this.isConnected) this.flush(); else this.cancel(); });
          }} />
        ${this.showSpinButtons ? html`<button class="lib-textbox-action" type="button" aria-label=${this.increaseLabel} ?disabled=${disabled || this.readonly} @mousedown=${(e: Event) => e.preventDefault()} @click=${() => this.send('up')}>+</button>
          <button class="lib-textbox-action" type="button" aria-label=${this.decreaseLabel} ?disabled=${disabled || this.readonly} @mousedown=${(e: Event) => e.preventDefault()} @click=${() => this.send('down')}>−</button>` : nothing}
      </div>
      ${this.message ? html`<div id="message" class="lib-textbox-message" role="alert">${this.message}</div>` : nothing}
    </div>`;
  }
}
